// Ruutujen muodot Passissa: maksimineliö, maksimiklusteri ja seuraavat tavoitteet.
//
// Luvut tulee palvelimelta (backend/app/shapes.py). Kortti näyttää ne yhdellä
// ruututasolla kerrallaan ja antaa kaksi jatkotoimea: näytä tavoite kartalla
// tai vie keräämättömät ruudut GPX- tai KML-tiedostona ajotietokonetta tai
// reittisovellusta varten.

import { useState } from "preact/hooks";

import { saveTextFile } from "../lib/files.js";
import { squareBounds } from "../lib/geo.js";
import { todayIso } from "../lib/format.js";
import { currentPosition } from "../lib/location.js";
import { modeLabel } from "../lib/modes.js";
import { missingText, modesText, sizeText, squareLabel } from "../lib/shapes.js";
import { Sheet } from "./Sheet.jsx";

const EXPORT_KINDS = [
  { id: "square", label: "Seuraavan neliön puuttuvat ruudut", hint: "Ruudut, jotka täyttämällä maksimineliö kasvaa." },
  { id: "cluster", label: "Klusterin kasvattajat", hint: "Ruudut, jotka kasvattavat maksimiklusteria eniten." },
  { id: "nearby", label: "Lähimmät uudet ruudut", hint: "Kerättyjen ruutujen viereiset keräämättömät ruudut, lähimmät ensin sijaintisi mukaan." },
];

export function ShapesCard({ app }) {
  const [level, setLevel] = useState("small");
  const [exporting, setExporting] = useState(false);
  const found = app.shapes?.[level];
  if (!found) return null;

  const { square, cluster, next_square: next } = found;
  const hint = found.cluster_hints?.[0];
  const showOnMap = () => {
    // Tavoite näkyviin: kartta tarkennetaan seuraavaan neliöön (tai nykyiseen, jos tavoitetta ei ole).
    const target = next ?? square;
    if (level === "large") {
      app.setOverlay("shapesLarge", true); // yksi kytkin: klusteri, neliö ja tavoite
    } else {
      app.setOverlay("shapes", true);
      app.setOverlay("next", Boolean(next));
    }
    app.showOnMap(squareBounds(found.zoom, target.x0, target.y0, target.size));
  };

  return (
    <section class="shape-card" aria-label="Ruutujen muodot">
      {/* Otsikko omalla rivillään, valitsin sen alla: kapealla näytöllä mikään ei katkea. */}
      <div class="shape-head">
        <h2>Ruutujen muodot</h2>
        <div class="chips" role="radiogroup" aria-label="Ruudun koko">
          <button class="chip" role="radio" aria-checked={level === "small"} onClick={() => setLevel("small")}>Pikkuruudut</button>
          <button class="chip" role="radio" aria-checked={level === "large"} onClick={() => setLevel("large")}>Suuret</button>
        </div>
      </div>
      {app.mapModes.length > 0 && (
        <p class="s shape-filter">Suodatus päällä: {modesText(app.mapModes, modeLabel).toLowerCase()}. Luvut lasketaan vain niistä ruuduista.</p>
      )}

      {!found.count ? (
        <p class="s" style={{ marginBottom: 0 }}>Ei vielä ruutuja. Neliö ja klusteri alkavat kasvaa ensimmäisestä retkestä.</p>
      ) : (
        <>
          <div class="shape-figs">
            <div>
              <b class="num">{squareLabel(square)}</b>
              <span>maksimineliö<br />{sizeText(square)}</span>
            </div>
            <div>
              <b class="num">{cluster.size}</b>
              <span>maksimiklusteri<br />{found.cluster_count > 1 ? `${found.cluster_count} erillistä` : "yhtenäinen"}</span>
            </div>
          </div>
          <ul class="shape-next">
            {next ? (
              <li>
                Seuraava neliö {next.size} × {next.size}: <b>{missingText(next.missing_count)}</b>, {sizeText(next).replace("noin ", "")}.
              </li>
            ) : (
              <li>Seuraavalle neliölle ei löydy paikkaa ilman mahdottomia ruutuja.</li>
            )}
            {hint && <li>Klusteri kasvaa nopeimmin yhdellä ruudulla: +{hint.gain}.</li>}
          </ul>
          <div class="sheet-actions">
            {level === "small" && <button class="btn btn-rasti" onClick={showOnMap}>Näytä kartalla</button>}
            <button class="btn" onClick={() => setExporting(true)}>Vie uudet ruudut</button>
          </div>
        </>
      )}
      {exporting && <TileExportSheet app={app} level={level} onClose={() => setExporting(false)} />}
    </section>
  );
}

function TileExportSheet({ app, level, onClose }) {
  const [kind, setKind] = useState("square");
  const [format, setFormat] = useState("gpx");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const where = kind === "nearby" ? await currentPosition(app.me) : {};
      const text = await app.api.exportTiles(format, { kind, level, ...where, limit: 25, modes: app.mapModes });
      const mime = format === "gpx" ? "application/gpx+xml" : "application/vnd.google-earth.kml+xml";
      await saveTextFile(`tilehunting-uudet-ruudut-${kind}-${todayIso()}.${format}`, text, mime);
      onClose();
    } catch (e) {
      app.toast({ kind: "info", what: "Vientiä ei saatu", sub: e.message });
    }
    setBusy(false);
  };

  return (
    <Sheet title="Vie keräämättömät ruudut" onClose={onClose}>
      <p class="hint">
        Tiedosto sisältää ruutujen keskipisteet reittipisteinä ja reunat viivoina. Mahdottomiksi merkittyjä ruutuja ei viedä.
      </p>
      <div class="radio-list" role="radiogroup" aria-label="Mitä viedään">
        {EXPORT_KINDS.map((k) => (
          <button key={k.id} role="radio" aria-checked={kind === k.id} class="layer-opt" onClick={() => setKind(k.id)}>
            <b>{k.label}</b>
            <span>{k.hint}</span>
          </button>
        ))}
      </div>
      <div class="field">
        <span class="label" id="fmt-label">Tiedostomuoto</span>
        <div class="chips" role="radiogroup" aria-labelledby="fmt-label">
          <button class="chip" role="radio" aria-checked={format === "gpx"} onClick={() => setFormat("gpx")}>GPX (ajotietokone, Komoot)</button>
          <button class="chip" role="radio" aria-checked={format === "kml"} onClick={() => setFormat("kml")}>KML (Google Earth, OsmAnd)</button>
        </div>
      </div>
      <div class="sheet-actions">
        <button class="btn btn-rasti btn-block" disabled={busy} onClick={run}>{busy ? "Haetaan…" : "Vie tiedosto"}</button>
        <button class="btn btn-block" onClick={onClose}>Peruuta</button>
      </div>
    </Sheet>
  );
}
